import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Chip,
  MenuItem,
  Select,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Grid,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert
} from '@mui/material';
import OrderLocationMap from '../components/OrderLocationMap';
import { Phone } from '@mui/icons-material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import {
  CardGiftcard as CardGiftcardIcon,
  ShoppingBag as ShoppingBagIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Home as AddressIcon,
  CreditCard as PaymentIcon,
  CalendarToday as DateIcon,
  Update as UpdateIcon,
  LocalOffer as ProductIcon,
  AttachMoney as PriceIcon,
  FormatListNumbered as QuantityIcon,
  Discount as DiscountIcon,
  Savings as SavingsIcon
} from '@mui/icons-material';
import api from '../services/api'
import { formatPrice } from '../components/CarrancyTrans';

const OrderDetails = ({keywordsData, language}) => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOrderDetails = async () => {
      try {
        const response = await api.get(`/orders/${orderId}`);
        setOrder(response.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
  };

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId]);

  const updateStatus = async (orderId, newStatus) => {
    try {
      await api.put(`/admin/orders/${orderId}/status`, { status: newStatus });
      fetchOrderDetails(); // Refresh data
    } catch (err) {
      console.error('Status update error:', err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'completed': return 'success';
      case 'canceled': return 'error';
      case 'succeeded': return 'success';
      case 'cancelled': return 'error';
      default: return 'primary';
    }
  };
  
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth' // Adds smooth scrolling animation
    });
  });

  const formatCustomization = (item) => {
  const parts = [];
  
  if (item.selected_size) {
    parts.push(`${language === 'ar' ? 'مقاس' : 'Size'}: ${language === 'ar' ? item.selected_size.name_ar || item.selected_size.name : item.selected_size.name_en || item.selected_size.name}`);
  }
  
  if (item.selected_extras && item.selected_extras.length > 0) {
    const extraNames = item.selected_extras.map(extra => 
      language === 'ar' ? extra.name_ar || extra.name : extra.name_en || extra.name
    ).join(', ');
    parts.push(`${language === 'ar' ? 'إضافات' : 'Extras'}: ${extraNames}`);
  }
  
  return parts.join(' | ');
};

const statusTxt = {
  canceled:language === 'ar' ? 'ملغي' : 'Canceled',
  pending: language === 'ar' ? 'معلق' : 'Pending',
  processing:language === 'ar' ? 'قيد التحضير' : 'Preparation',
  shipped: language === 'ar' ? 'قيد التوصيل' : 'On road',
  delivered: language === 'ar' ? 'مكتمل' : 'Complete'
};

  const formatPaymentMethod = (method) => {
    return method.replace(/_/g, ' ');
  };

  // Calculate total savings from order items
  const totalSavings = order?.items?.reduce((total, item) => {
    const originalPrice = item.original_price || item.price;
    return total + (originalPrice - item.price) * item.quantity;
  }, 0) || 0;

  // Calculate original total before discounts
  const originalTotal = order?.items?.reduce((total, item) => {
    return total + (item.original_price || item.price) * item.quantity;
  }, 0) || 0;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress color='secondary'/>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!order) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4">{language === 'ar' ? 'لم يتم العثور على الطلب' : 'Order not found'}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, mb: 10 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
          <ShoppingBagIcon />
        </Avatar>&nbsp;
        <Typography variant="h4"># {language === 'ar' ? 'طلب رقم' : 'Order ID'} {order.id}</Typography>
        <Chip
          label={statusTxt[order.status]}
          color={getStatusColor(order.status)}
          sx={{ ml: 2, textTransform: 'capitalize' }}
        />
      </Box>

      {/* Savings Banner */}
      {totalSavings > 0 && (
        <Paper elevation={2} sx={{ 
          p: 3, 
          mb: 3, 
          backgroundColor: 'success.light',
          background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SavingsIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {language === 'ar' ? '🎉 العميل قام بتوفير ' : '🎉 The customer save '} {formatPrice(totalSavings.toFixed(2),order.currency,language)}!
              </Typography>
              <Typography variant="body2">
                 {language === 'ar' ? 'تبلغ نسبة الخصم ' : 'The discount rate '} {((totalSavings / originalTotal) * 100).toFixed(1)} {language === 'ar' ? '% من هاذا الطلب' : '% from this order'} 
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Savings Banner */}
      {order.points_used > 0 && (
        <Paper elevation={2} sx={{ 
          p: 3, 
          mb: 3, 
          backgroundColor: 'success.light',
          background: 'linear-gradient(135deg, #72671e 0%, #7e8f10 100%)',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AccountBalanceWalletIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {language === 'ar' ? '🎉 العميل قام بإستخدام ' : '🎉 The customer use '} {order.points_used} {language === 'ar' ? ' نقطة':' Point'}!
              </Typography>
              <Typography variant="body2">
                 {language === 'ar' ? 'تبلغ قيمة الخصم ' : 'The discount value '} {formatPrice(order.points_used_value.toFixed(2),order.currency,language)}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      <Grid container spacing={3}>
                {/* Shipping & Payment */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <AddressIcon sx={{ mr: 1 }} /> {language === 'ar' ? 'معلومات التوصيل / الدفع' : 'Delivery/Payment Information'}
            </Typography>
            <List>
              <ListItem>
                {/* Google Map */}
                <OrderLocationMap
                   latitude={order.latitude}
                   longitude={order.longitude}
                   height="300px"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <AddressIcon />
                </ListItemIcon>
                <ListItemText primary={order.shipping_address} secondary={language === 'ar' ? 'عنوان التوصيل' : 'Delivery address'} />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <PaymentIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={formatPaymentMethod(order.payment_method)} 
                  secondary={language === 'ar' ? 'طريقة الدفع' : 'Payment Method'}
                />
                {order.visa_payment_transaction ? (
  <Chip 
    label={order.visa_payment_transaction.status}
    color={getStatusColor(order.visa_payment_transaction.status)}
  />
) : (
  <Chip 
    label={order.payment_method === 'cash_on_delivery' ? 'Cash' : 'No Payment'}
    color="default"
  />
)}
              </ListItem>
              <ListItem>
                <FormControl size="small" fullWidth>
                    <Select
                        disabled = {order.status === 'delivered'|| order.status === 'canceled'}
                        value={order.status}
                        onChange={(e) => updateStatus(order.id, e.target.value)}
                    >
                        <MenuItem value="pending">{language === 'ar' ? 'معلقة' : 'Pending'}</MenuItem>
                        <MenuItem value="processing">{language === 'ar' ? 'قيد التحضير' : 'Preparation'}</MenuItem>
                        <MenuItem value="shipped">{language === 'ar' ? 'قيد التوصيل' : 'On road'}</MenuItem>
                        <MenuItem value="delivered">{language === 'ar' ? 'مكتمل' : 'Complete'}</MenuItem>
                        <MenuItem value="canceled">{language === 'ar' ? 'ملغي' : 'Canceled'}</MenuItem>
                    </Select>
                </FormControl>
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Customer Information */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonIcon sx={{ mr: 1 }} /> {language === 'ar' ? 'معلومات العميل' : 'Customer Information'}
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <PersonIcon />
                </ListItemIcon>
                <ListItemText primary={order.user?.username} secondary={language === 'ar' ? 'إسم العميل' : 'Customer Name'} />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <EmailIcon />
                </ListItemIcon>
                <ListItemText primary={order.user?.email} secondary={language === 'ar' ? 'الإيميل' : 'Customer E-mail'} />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Phone />
                </ListItemIcon>
                <ListItemText primary={order.user?.user_phone} secondary={language === 'ar' ? 'رقم الهاتف' : 'Phone No'} />
              </ListItem>
            </List>
          </Paper>
        </Grid>

        {/* Order Timeline */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <DateIcon sx={{ mr: 1 }} /> {language === 'ar' ? 'الجدول الزمني للطلب' : 'Order timeline'}
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <DateIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={new Date(order.created_at).toLocaleString()} 
                  secondary={language === 'ar' ? 'تم إنشاءه' : 'Created at'}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <UpdateIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={new Date(order.updated_at).toLocaleString()} 
                  secondary={language === 'ar' ? 'أخر تحديث' : 'Last update'}
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>

{/* Order Items */}
{/* Order Items */}
<Grid item xs={12}>
  <Paper elevation={3} sx={{ p: 2 }}>
    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
      <ProductIcon sx={{ mr: 1 }} /> {language === 'ar' ? 'محتوى السلة' : 'Basket contents'}
    </Typography>
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{language === 'ar' ? 'إسم المنتج' : 'Product Name'}</TableCell>
            <TableCell align="right">{language === 'ar' ? 'الكمية' : 'Quantity'}</TableCell>
            <TableCell align="right">{language === 'ar' ? 'سعر الحبة' : 'Unit price'}</TableCell>
            <TableCell align="right">{language === 'ar' ? 'خصم' : 'Discount'}</TableCell>
            <TableCell align="right">{language === 'ar' ? 'الإجمالي' : 'Total'}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {order.items.map(item => {
            const originalPrice = item.original_price || item.price;
            const isDiscounted = item.discount_amount > 0;
            const discountAmount = item.discount_amount || 0;
            const discountPercent = item.discount_percent || 0;
            
            // Check if this is a free gift item
            const isFreeGift = item.is_free_item || item.free_reward_item_id;
            
            // Get promotion info from item
            const hasPromotion = item.applied_promotion_data;
            const promotionData = item.applied_promotion_data;
            const promotionRewardName = promotionData?.reward_name;
            const qualifyingAmount = promotionData?.qualifying_amount || item.promotion_qualifying_amount;
            const promotionType = promotionData?.type;
            const promotionThreshold = promotionData?.threshold;

            return (
              <TableRow key={item.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <ProductIcon color="action" sx={{ mr: 1 }} />
                      <Typography component="span">
                        {language === 'ar' ? item.product.name : item.product.name_en}
                      </Typography>
                      
                      {/* Customization Badge */}
                      {item.has_customizations && (
                        <Chip
                          label={language === 'ar' ? 'مخصص' : 'Custom'}
                          size="small"
                          sx={{ 
                            backgroundColor: '#FFD700',
                            color: '#000000',
                            fontWeight: 'bold',
                            height: 20,
                            fontSize: '0.7rem'
                          }}
                        />
                      )}
                      
                      {/* Free Gift Badge - Only show on the main product that earned the gift */}
                      {hasPromotion && !isFreeGift && (
                        <Chip
                          icon={<CardGiftcardIcon sx={{ fontSize: 14 }} />}
                          label={language === 'ar' ? 'يؤهل لهدية' : 'Qualifies for Gift'}
                          size="small"
                          sx={{ 
                            backgroundColor: '#FF9800',
                            color: '#FFFFFF',
                            fontWeight: 'bold',
                            height: 20,
                            fontSize: '0.7rem'
                          }}
                        />
                      )}
                      
                      {/* Free Gift Badge - Show on the actual free item */}
                      {isFreeGift && (
                        <Chip
                          icon={<CardGiftcardIcon sx={{ fontSize: 14 }} />}
                          label={language === 'ar' ? 'هدية مجانية' : 'Free Gift'}
                          size="small"
                          sx={{ 
                            backgroundColor: '#4CAF50',
                            color: '#FFFFFF',
                            fontWeight: 'bold',
                            height: 20,
                            fontSize: '0.7rem'
                          }}
                        />
                      )}
                    </Box>
                    
                    {/* Customization Details */}
                    {item.has_customizations && (
                      <Box sx={{ mt: 0.5, ml: 4 }}>
                        <Typography variant="caption" sx={{ color: '#FFD700', display: 'block' }}>
                          {item.selected_size && (
                            <span>{language === 'ar' ? 'مقاس' : 'Size'}: {language === 'ar' ? item.selected_size.name_ar || item.selected_size.name : item.selected_size.name_en || item.selected_size.name} </span>
                          )}
                          {item.selected_extras && item.selected_extras.length > 0 && (
                            <span>
                              {item.selected_size ? '| ' : ''}
                              {language === 'ar' ? 'إضافات' : 'Extras'}: {item.selected_extras.map(e => language === 'ar' ? e.name_ar || e.name : e.name_en || e.name).join(', ')}
                            </span>
                          )}
                        </Typography>
                        {item.customization_price > 0 && !isFreeGift && (
                          <Typography variant="caption" sx={{ color: '#4CAF50' }}>
                            +{formatPrice(item.customization_price.toFixed(2),order.currency,language)} {language === 'ar' ? 'إضافات' : 'extras'}
                          </Typography>
                        )}
                      </Box>
                    )}
                    
                    {/* Promotion Details - Show on the main product */}
                    {hasPromotion && !isFreeGift && (
                      <Box sx={{ mt: 0.5, ml: 4 }}>
                        <Typography variant="caption" sx={{ color: '#FF9800', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CardGiftcardIcon sx={{ fontSize: 14 }} />
                          {language === 'ar' 
                            ? `أنفق ${qualifyingAmount || promotionThreshold} عملة إضافية واحصل على ${promotionRewardName || 'هدية'} مجاناً` 
                            : `Spend ${qualifyingAmount || promotionThreshold} $ extra and get free ${promotionRewardName || 'gift'}`}
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Free Gift Item Display - Show what promotion this came from */}
                    {isFreeGift && promotionData && (
                      <Box sx={{ mt: 0.5, ml: 4 }}>
                        <Typography variant="caption" sx={{ color: '#4CAF50', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CardGiftcardIcon sx={{ fontSize: 14 }} />
                          {language === 'ar' 
                            ? `هدية من عرض ${promotionRewardName || ''}`
                            : `Gift from ${promotionRewardName || ''} promotion`}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </TableCell>
                
                <TableCell align="right">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <QuantityIcon color="action" sx={{ mr: 1 }} />
                    {item.quantity}
                  </Box>
                </TableCell>
                
                <TableCell align="right">
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <PriceIcon color="action" sx={{ mr: 1 }} />
                    {isDiscounted ? (
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" color="error" fontWeight="bold">
                          {formatPrice(item.price,order.currency,language)}
                        </Typography>
                        <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                          {formatPrice(originalPrice.toFixed(2),order.currency,language)}
                        </Typography>
                      </Box>
                    ) : (
                      `${formatPrice(item.price,order.currency,language)}`
                    )}
                  </Box>
                </TableCell>
                
                <TableCell align="right">
                  { isDiscounted ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <DiscountIcon color="success" sx={{ mr: 0.5 }} />
                      <Typography variant="body2" color="success.main">
                        - {formatPrice(discountAmount.toFixed(2),order.currency,language)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                        ({discountPercent}%)
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      -
                    </Typography>
                  )}
                </TableCell>
                
                <TableCell align="right">
                  {isFreeGift ? (
                    <>
                      <Typography variant="body2" fontWeight="bold">
                        {formatPrice((item.quantity * parseFloat(item.price)).toFixed(2),order.currency,language)}
                      </Typography>
                      {isDiscounted && (
                        <Typography variant="caption" color="success.main" display="block">
                          {language === 'ar' ? 'وفر' : 'Saved'} {formatPrice((discountAmount * item.quantity).toFixed(2),order.currency,language)}
                        </Typography>
                      )}
                    <Typography variant="body2" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>
                      {language === 'ar' ? 'مجاني' : 'FREE'}
                    </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" fontWeight="bold">
                        {formatPrice((item.quantity * parseFloat(item.price)).toFixed(2),order.currency,language)}
                      </Typography>
                      {isDiscounted && (
                        <Typography variant="caption" color="success.main" display="block">
                          {language === 'ar' ? 'وفر' : 'Saved'} {formatPrice((discountAmount * item.quantity).toFixed(2),order.currency,language)}
                        </Typography>
                      )}
                    </>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
          
          {/* Promotion Summary - Extract unique promotions from items */}
          {order.items.some(item => item.applied_promotion_data) && (
            <TableRow>
              <TableCell colSpan={5} sx={{ backgroundColor: 'rgba(255, 215, 0, 0.05)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, flexWrap: 'wrap' }}>
                  <CardGiftcardIcon sx={{ color: '#FFD700' }} />
                  <Typography variant="subtitle2" sx={{ color: '#FFD700' }}>
                    {language === 'ar' ? 'العروض الترويجية المطبقة' : 'Applied Promotions'}:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {/* Get unique promotions from items */}
                    {[...new Set(order.items
                      .filter(item => item.applied_promotion_data)
                      .map(item => item.applied_promotion_data.reward_name)
                    )].map((rewardName, index) => (
                      <Chip
                        key={index}
                        size="small"
                        label={rewardName || (language === 'ar' ? 'عرض' : 'Promotion')}
                        sx={{ backgroundColor: '#FFD700', color: '#000000', fontWeight: 600 }}
                      />
                    ))}
                  </Box>
                </Box>
              </TableCell>
            </TableRow>
          )}
          
          {/* Order Summary */}
          {totalSavings > 0 && (
            <>
              <TableRow>
                <TableCell colSpan={4} align="right">
                  <Typography variant="body2">{language === 'ar' ? 'المجموع الفرعي (الأصلي)' : 'Subtotal (original)'}:</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" sx={{ textDecoration: 'line-through' }}>
                    {formatPrice(originalTotal.toFixed(2),order.currency,language)}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={4} align="right">
                  <Typography variant="body2" color="success.main">
                    <DiscountIcon sx={{ fontSize: 16, verticalAlign: 'text-bottom', mr: 0.5 }} />
                    {language === 'ar' ? 'إجمالي الخصم' : 'Total Discount'} :
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color="success.main">
                    - {formatPrice(totalSavings.toFixed(2),order.currency,language)} 
                  </Typography>
                </TableCell>
              </TableRow>
            </>
          )}
          
          {/* Order Summary */}
          {order.points_used > 0 && (
            <>
              <TableRow>
                <TableCell colSpan={4} align="right">
                  <Typography variant="body2" color="success.main">
                    <DiscountIcon sx={{ fontSize: 16, verticalAlign: 'text-bottom', mr: 0.5 }} />
                    {language === 'ar' ? 'إجمالي خصم النقاط' : 'Total Points Discount'} :
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" color="success.main">
                    - {formatPrice(order.points_used_value.toFixed(2),order.currency,language)}
                  </Typography>
                </TableCell>
              </TableRow>
            </>
          )}

          {/* Free Items Summary */}
          {order.free_items_count > 0 && (
            <TableRow>
              <TableCell colSpan={4} align="right">
                <Typography variant="body2" sx={{ color: '#4CAF50', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                  <CardGiftcardIcon sx={{ fontSize: 16 }} />
                  {language === 'ar' ? 'العناصر المجانية' : 'Free Items'} :
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>
                  {order.free_items_count} {language === 'ar' ? 'عنصر' : 'item(s)'}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          
          {/* Customization Summary */}
          {order.customization_summary?.total_customized_items > 0 && (
            <TableRow>
              <TableCell colSpan={4} align="right">
                <Typography variant="body2" sx={{ color: '#FFD700' }}>
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                    <span>✨</span>
                    {language === 'ar' ? 'إجمالي قيمة الإضافات' : 'Total Extras Value'}:
                  </Box>
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" sx={{ color: '#FFD700', fontWeight: 'bold' }}>
                  +{formatPrice(order.customization_summary.total_customization_price,order.currency,language)}
                </Typography>
              </TableCell>
            </TableRow>
          )}

          {/* service charge Total */}
          <TableRow>
              <TableCell colSpan={4} align="right">
                <Typography variant="body2">
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                    {language === 'ar' ? 'رسوم الخدمة' : 'Service charge'}:
                  </Box>
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" sx={{ color: '#FFD700', fontWeight: 'bold' }}>
                  {formatPrice(order.service_charge,order.currency,language)}
                </Typography>
              </TableCell>
          </TableRow>

          {/* delivery Total */}
          <TableRow>
            <TableCell colSpan={4} align="right">
              <Typography variant="body2">
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                    {language === 'ar' ? 'رسوم التوصيل' : 'Delivery fees'} :
                  </Box>
                </Typography>
            </TableCell>
            <TableCell align="right">
                <Typography variant="body2" sx={{ color: '#FFD700', fontWeight: 'bold' }}>
                  {formatPrice(order.delivery_fee,order.currency,language)}
                </Typography>
            </TableCell>
          </TableRow>

          {/* Grand Total */}
          <TableRow sx={{ '& td': { borderBottom: 'none' } }}>
            <TableCell colSpan={4} align="right">
              <Typography variant="subtitle1" fontWeight="bold">
                {language === 'ar' ? 'إجمالي الطلب' : 'Total order'} :
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography 
                variant="h6" 
                color={totalSavings > 0 ? 'success.main' : '#FFD700'}
                fontWeight="bold"
              >
                {formatPrice(order.total_amount,order.currency,language)}
              </Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  </Paper>
</Grid></Grid>
    </Box>
  );
};

export default OrderDetails;